import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useNuxtApp } from 'nuxt/app';
import type { DeepPartial, Schema } from '~~/common/types/api';

type CacheSegmentationModel = DeepPartial<Schema<'CacheSegmentationModel'>>;

export const useAwCacheKeyStore = defineStore('awCacheKey', () => {
  const nuxtApp = useNuxtApp();
  const cacheKeyLegacy = ref('');
  const cacheKeySegmentation = ref('');
  const companyNo = ref('');
  const isCacheSegmentationFetched = ref(false);
  let cacheSegmentationFetch: Promise<void> | null = null;

  function setCacheKeyLegacy (payload: string) {
    cacheKeyLegacy.value = payload;
  }
  function setCacheKeySegmentation (payload: string) {
    cacheKeySegmentation.value = payload;
  }

  async function fetchCacheSegmentation () {
    const { $api, $logger } = nuxtApp;
    if (!cacheSegmentationFetch) {
      cacheSegmentationFetch = (async () => {
        try {
          const result = await $api.$get<CacheSegmentationModel>('/cache-segmentation');
          if (result?.cacheSegmentationCode) {
            setCacheKeySegmentation(`${result.cacheSegmentationCode}`);
          }
          companyNo.value = result?.companyNo || '';
          isCacheSegmentationFetched.value = true;
        } catch (error) {
          $logger.error(error);
        } finally {
          cacheSegmentationFetch = null;
        }
      })();
    }
    return cacheSegmentationFetch;
  }

  async function ensureCacheSegmentation () {
    if (!isCacheSegmentationFetched.value) {
      await fetchCacheSegmentation();
    }
  }

  // Force a re-fetch (e.g. after the delivery area changes, the companyNo may differ).
  async function refreshCacheSegmentation () {
    isCacheSegmentationFetched.value = false;
    await fetchCacheSegmentation();
  }

  return {
    cacheKeyLegacy,
    cacheKeySegmentation,
    companyNo,
    isCacheSegmentationFetched,
    setCacheKeyLegacy,
    setCacheKeySegmentation,
    fetchCacheSegmentation,
    ensureCacheSegmentation,
    refreshCacheSegmentation,
  };
});
